import { nodeToMml } from "@corca/latex";
import { comparer, computed, observable, reaction, runInAction } from "mobx";

import { loadSpeechEngine } from "@/components/expression/utils/loadSpeechEngine";
import { scheduleOnFrame } from "@/utils/scheduleOnFrame";

import type { SpeechEngine } from "@/components/expression/utils/loadSpeechEngine";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { NodeId, NodeJson } from "@corca/data";

const MATHML_NAMESPACE = "http://www.w3.org/1998/Math/MathML";

export type MathMlAnnotation = {
  element: HTMLElement;
  destroy: () => void;
};

function asNodeJson(node: NodeModel): NodeJson {
  return node as unknown as NodeJson;
}

/** Serializes `node` as a standalone `<math>` element, or `""` when it has no math to read. */
export function nodeToMathMl(node: NodeModel): string {
  const expression = nodeToMml(asNodeJson(node));
  return expression ? `<math xmlns="${MATHML_NAMESPACE}">${expression}</math>` : "";
}

const speechEngine = observable.box<SpeechEngine | undefined>(undefined, { deep: false });
let speechEngineRequested = false;

function requestSpeechEngine() {
  if (speechEngineRequested) return;
  speechEngineRequested = true;

  loadSpeechEngine().then(
    (engine) => runInAction(() => speechEngine.set(engine)),
    (error: unknown) => {
      speechEngineRequested = false;
      console.error("Failed to load the math speech engine", error);
    },
  );
}

type AnnotationContent = { speech: string } | { mathMl: string; awaitingSpeech?: true };

/**
 * A screen-reader-only rendering of an expression, kept in sync with it: spoken text while the
 * document is editable, MathML while it is read-only or the speech engine hasn't loaded.
 *
 * NVDA presumably struggles reading `<math>` element in contenteditable elements, so editors
 * get plain words.
 */
export function createMathMlAnnotation(doc: DocumentModel, nodeId: NodeId): MathMlAnnotation {
  const element = document.createElement("span");
  element.className = "sr-only static m-0 inline-block size-0 align-top";

  const mathMl = computed(() => {
    const node = doc.nodes.get(nodeId);
    return node ? nodeToMathMl(node) : "";
  });

  const content = computed<AnnotationContent>(
    () => {
      if (!doc.editable) return { mathMl: mathMl.get() };

      const engine = speechEngine.get();
      const value = mathMl.get();
      // Until the engine loads, or if it fails to, MathML is still something to read.
      if (!engine) return { mathMl: value, awaitingSpeech: true };
      return { speech: value ? engine.toSpeech(value) : "" };
    },
    { equals: comparer.structural },
  );

  const render = (next: AnnotationContent) => {
    if ("speech" in next) {
      element.textContent = next.speech;
      return;
    }

    element.innerHTML = next.mathMl;
    if (next.awaitingSpeech) {
      requestSpeechEngine();
    }
  };

  let dispose: (() => void) | undefined;

  const start = () => {
    dispose = reaction(() => content.get(), render, {
      fireImmediately: true,
      // Rebuilding the element and generating speech both cost more than serializing, so let
      // them land after the keystroke.
      scheduler: scheduleOnFrame,
    });
  };

  // Speech generation re-parses the whole expression and a document opens with all its
  // expressions at once, so in an editable document the first run also waits for a frame.
  let cancelStart = () => {};
  if (doc.editable) {
    cancelStart = scheduleOnFrame(start);
  } else {
    start();
  }

  return {
    element,
    destroy: () => {
      cancelStart();
      dispose?.();
    },
  };
}
